import {
  isRecordVisible,
  type ScopeCaller,
  type ScopeDirectory,
} from '@web/modules/customers/data-scope';
import { addDays, startOfWeek, weekdayOf } from '@web/shared/util/zoned-time';
import {
  compareByDue,
  isTaskOverdue,
  TASK_PRIORITIES,
  type Task,
  type TaskCountsQuery,
  type TaskList,
  type TaskListQuery,
  type TaskPriority,
  type TaskType,
  type UpdateTaskInput,
} from '../contract';

/**
 * The tasks board (`/tasks?display=kanban`, spec 006 §7.2): the same tasks as the list, in
 * columns by one field — the due day, the assignee, the type or the priority. The rules here
 * are pure (the screen and the tests use them): which columns exist and what each one asks
 * the API, what dropping a card on a column changes, and how the cached columns follow a
 * move while the API answers.
 */

export const TASK_GROUPS = ['due', 'assignee', 'type', 'priority'] as const;
export type TaskGroup = (typeof TASK_GROUPS)[number];

export const DUE_COLUMNS = ['overdue', 'today', 'tomorrow', 'this-week', 'later'] as const;
export type DueColumn = (typeof DUE_COLUMNS)[number];

/** The id of the column of completed tasks (shown on request, always the last one). */
export const DONE_COLUMN = 'done';

/** Cards each column asks for at a time ("Carregar mais" asks for this many more). */
export const KANBAN_PAGE_SIZE = 20;

/* ------------------------------------------------------------------ due days */

export interface DueRanges {
  today: string;
  tomorrow: string;
  /** From the day after tomorrow to Sunday; `null` when the week ends tomorrow or before. */
  week: { from: string; to: string } | null;
  /** The first day of "Mais tarde": the Monday after this week (never before "this week"). */
  laterFrom: string;
}

/**
 * The days of the due columns, from today in the organization's zone. Weeks start on Monday,
 * so on Saturday and Sunday "Esta semana" has no day left after tomorrow and does not show.
 */
export function dueRanges(today: string): DueRanges {
  const tomorrow = addDays(today, 1);
  const sunday = addDays(startOfWeek(today), 6);
  const weekFrom = addDays(today, 2);
  const week = weekFrom <= sunday ? { from: weekFrom, to: sunday } : null;
  const nextMonday = addDays(sunday, 1);
  return { today, tomorrow, week, laterFrom: nextMonday > weekFrom ? nextMonday : weekFrom };
}

/** The due column of an open task's day. */
export function dueColumnOf(dueDate: string, today: string): DueColumn {
  const ranges = dueRanges(today);
  if (dueDate < today) return 'overdue';
  if (dueDate === today) return 'today';
  if (dueDate === ranges.tomorrow) return 'tomorrow';
  if (ranges.week && dueDate <= ranges.week.to) return 'this-week';
  return 'later';
}

const isBusinessDay = (date: string) => {
  const weekday = weekdayOf(date);
  return weekday >= 1 && weekday <= 5;
};

/**
 * Where a card dropped on "Esta semana" goes: the first business day of the column (from the
 * day after tomorrow to Sunday) — or its first day, when only the weekend is left.
 */
export function thisWeekTarget(week: { from: string; to: string }): string {
  for (let day = week.from; day <= week.to; day = addDays(day, 1)) {
    if (isBusinessDay(day)) return day;
  }
  return week.from;
}

/* ------------------------------------------------------------------ columns */

/** What a drop on a column changes; `none` = the column takes no cards. */
export type DropTarget =
  | { kind: 'due-date'; dueDate: string }
  | { kind: 'pick-date'; min: string }
  | { kind: 'assignee'; membershipId: string; name: string }
  | { kind: 'type'; type: TaskType }
  | { kind: 'priority'; priority: TaskPriority }
  | { kind: 'none'; reason: 'overdue' | 'done' };

export interface KanbanColumnDef {
  /** `due:today`, `assignee:<membershipId>`, `type:<id>`, `priority:high` or `done`. */
  id: string;
  title: string;
  /** The column's slice of the list, on top of the screen's filters (no page size). */
  query: TaskListQuery;
  target: DropTarget;
  /** A column that only shows while it has cards (an inactive type). */
  hideWhenEmpty?: boolean;
}

type Translate = (key: string, params?: Record<string, unknown>) => string;

export interface KanbanColumnsContext {
  groupBy: TaskGroup;
  today: string;
  /** The screen's filters (scope, type, priority, record, assignee). */
  filters: TaskCountsQuery;
  /** The task types, in the configured order. */
  types: readonly TaskType[];
  /** The assignees of the board: the caller first, then whom they reach. */
  assignees: readonly { membershipId: string; name: string }[];
  /** The caller's membership (the only column of "Minhas" grouped by assignee). */
  me: { membershipId: string; name: string } | null;
  showCompleted: boolean;
  t: Translate;
}

const open = (filters: TaskCountsQuery, slice: Partial<TaskListQuery>): TaskListQuery => ({
  ...filters,
  status: 'open',
  ...slice,
});

function dueColumns({ today, filters, t }: KanbanColumnsContext): KanbanColumnDef[] {
  const ranges = dueRanges(today);
  const columns: KanbanColumnDef[] = [
    {
      id: 'due:overdue',
      title: t('tasks.kanban.due.overdue'),
      query: { ...filters, bucket: 'overdue' },
      target: { kind: 'none', reason: 'overdue' },
    },
    {
      id: 'due:today',
      title: t('tasks.kanban.due.today'),
      query: { ...filters, bucket: 'today' },
      target: { kind: 'due-date', dueDate: today },
    },
    {
      id: 'due:tomorrow',
      title: t('tasks.kanban.due.tomorrow'),
      query: open(filters, { from: ranges.tomorrow, to: ranges.tomorrow }),
      target: { kind: 'due-date', dueDate: ranges.tomorrow },
    },
  ];
  if (ranges.week) {
    columns.push({
      id: 'due:this-week',
      title: t('tasks.kanban.due.this-week'),
      query: open(filters, { from: ranges.week.from, to: ranges.week.to }),
      target: { kind: 'due-date', dueDate: thisWeekTarget(ranges.week) },
    });
  }
  columns.push({
    id: 'due:later',
    title: t('tasks.kanban.due.later'),
    query: open(filters, { from: ranges.laterFrom }),
    target: { kind: 'pick-date', min: ranges.laterFrom },
  });
  return columns;
}

function assigneeColumns({ filters, assignees, me }: KanbanColumnsContext): KanbanColumnDef[] {
  // "Minhas" are the caller's tasks only: one column.
  const people =
    filters.scope === 'team'
      ? assignees.filter(
          (person) =>
            !filters.assigneeMembershipId || person.membershipId === filters.assigneeMembershipId,
        )
      : me
        ? [me]
        : [];
  return people.map((person) => ({
    id: `assignee:${person.membershipId}`,
    title: person.name,
    query:
      filters.scope === 'team'
        ? open(filters, { assigneeMembershipId: person.membershipId })
        : open(filters, {}),
    target: { kind: 'assignee', membershipId: person.membershipId, name: person.name },
  }));
}

function typeColumns({ filters, types }: KanbanColumnsContext): KanbanColumnDef[] {
  return types
    .toSorted((a, b) => a.order - b.order)
    .filter((type) => !filters.typeId || type.id === filters.typeId)
    .map((type) => ({
      id: `type:${type.id}`,
      title: type.name,
      query: open(filters, { typeId: type.id }),
      target: { kind: 'type', type },
      // An inactive type still holds its old tasks, but takes no new one (rule 3).
      hideWhenEmpty: !type.active,
    }));
}

function priorityColumns({ filters, t }: KanbanColumnsContext): KanbanColumnDef[] {
  return TASK_PRIORITIES.filter(
    (priority) => !filters.priority || priority === filters.priority,
  ).map((priority) => ({
    id: `priority:${priority}`,
    title: t(`tasks.priorities.${priority}`),
    query: open(filters, { priority }),
    target: { kind: 'priority', priority },
  }));
}

/** The columns of the board, in order; "Concluídas" closes it when asked for. */
export function kanbanColumns(context: KanbanColumnsContext): KanbanColumnDef[] {
  const columns =
    context.groupBy === 'due'
      ? dueColumns(context)
      : context.groupBy === 'assignee'
        ? assigneeColumns(context)
        : context.groupBy === 'type'
          ? typeColumns(context)
          : priorityColumns(context);
  if (context.showCompleted) {
    columns.push({
      id: DONE_COLUMN,
      title: context.t('tasks.kanban.done'),
      query: { ...context.filters, bucket: 'done' },
      target: { kind: 'none', reason: 'done' },
    });
  }
  return columns;
}

/* ------------------------------------------------------------------ dropping */

/**
 * Whether the caller may change a task (rule 15): theirs, created by them or — beyond the
 * "own" scope — one whose assignee they reach.
 */
export function canManageTask(
  task: Pick<Task, 'assignee' | 'createdBy' | 'unit'>,
  caller: ScopeCaller,
  directory: ScopeDirectory,
): boolean {
  if (task.assignee.membershipId === caller.membershipId) return true;
  if (task.createdBy?.membershipId === caller.membershipId) return true;
  if (caller.dataScope === 'own') return false;
  return isRecordVisible(caller, directory, {
    ownerMembershipId: task.assignee.membershipId,
    unitId: task.unit.id,
  });
}

export type DropRejection =
  | 'overdue'
  | 'done'
  | 'not-open'
  | 'no-permission'
  | 'not-manageable'
  | 'assignee-out-of-reach'
  | 'type-inactive';

export type DropDecision =
  /** Dropped where it already was: nothing changes. */
  | { kind: 'stay' }
  | { kind: 'reject'; reason: DropRejection }
  /** "Mais tarde": the screen asks for the day first. */
  | { kind: 'pick-date'; min: string }
  /** The change to send and the card as it will look. */
  | { kind: 'update'; input: UpdateTaskInput; patch: Partial<Task> };

/** What dropping `task` from column `from` on column `to` does. */
export function dropDecision(
  task: Task,
  from: Pick<KanbanColumnDef, 'id'>,
  to: Pick<KanbanColumnDef, 'id' | 'target'>,
  rights: { canUpdate: boolean; manageable: boolean; canAssign: (membershipId: string) => boolean },
): DropDecision {
  if (from.id === to.id) return { kind: 'stay' };
  if (to.target.kind === 'none') return { kind: 'reject', reason: to.target.reason };
  if (task.status !== 'open') return { kind: 'reject', reason: 'not-open' };
  if (!rights.canUpdate) return { kind: 'reject', reason: 'no-permission' };
  if (!rights.manageable) return { kind: 'reject', reason: 'not-manageable' };
  const target = to.target;
  switch (target.kind) {
    case 'pick-date':
      return { kind: 'pick-date', min: target.min };
    case 'due-date':
      return {
        kind: 'update',
        // Only the day: the API keeps the time of day (or the task stays all-day).
        input: { dueDate: target.dueDate },
        patch: { dueDate: target.dueDate },
      };
    case 'assignee':
      if (!rights.canAssign(target.membershipId)) {
        return { kind: 'reject', reason: 'assignee-out-of-reach' };
      }
      return {
        kind: 'update',
        input: { assigneeMembershipId: target.membershipId },
        patch: { assignee: { membershipId: target.membershipId, name: target.name } },
      };
    case 'type':
      if (!target.type.active) return { kind: 'reject', reason: 'type-inactive' };
      return {
        kind: 'update',
        input: { typeId: target.type.id },
        patch: {
          type: {
            id: target.type.id,
            name: target.type.name,
            icon: target.type.icon,
            color: target.type.color,
          },
        },
      };
    case 'priority':
      return {
        kind: 'update',
        input: { priority: target.priority },
        patch: { priority: target.priority },
      };
  }
}

/** The card after a move (the overdue flag follows a new day). */
export function movedTask(
  task: Task,
  patch: Partial<Task>,
  timeZone: string,
  now: Date = new Date(),
): Task {
  const next = { ...task, ...patch };
  return { ...next, overdue: isTaskOverdue(next, timeZone, now) };
}

/* ------------------------------------------------------------------ the cache */

/** The column without the task (its total one less). Unknown task: unchanged. */
export function withoutTask(list: TaskList, taskId: string): TaskList {
  if (!list.items.some((item) => item.id === taskId)) return list;
  return {
    ...list,
    items: list.items.filter((item) => item.id !== taskId),
    total: Math.max(0, list.total - 1),
  };
}

/** The column with the task in its place by due (its total one more). */
export function withTask(list: TaskList, task: Task): TaskList {
  const items = [...list.items.filter((item) => item.id !== task.id), task].toSorted(
    (a, b) => compareByDue(a, b) || a.createdAt.localeCompare(b.createdAt),
  );
  const added = list.items.some((item) => item.id === task.id) ? 0 : 1;
  return { ...list, items, total: list.total + added };
}
